import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Subscription } from "react-apollo";

import { httpClient } from "../../settings/apolloClient";
import { InstrumentsPanelWrapper, InfoParagraph } from "../../components/StyledComponents";
import { API } from "../../constants/shipmentType";
import AlertModal from "../../components/AlertModal/AlertModal";

import InstrumentsPanelContent from "./InstrumentsPanelContent";
import LOADING_CONCRETE_SHIPMENT from "./graphql/queries/loadingConcreteShipment.js";
import PLANT_LOCATIONS_QUERY from "./graphql/queries/plantLocations";
import ControlMenu from "./ControlMenu/ControlMenu";
import { ManualOperation } from "./ManualOperation/ManualOperation";

import { CURRENT_AUTOMATION_STATUS_QUERY } from "../AutomationStatus/automationStatusQueries";

import { Sensors } from "./Sensors/Sensors";
import SENSORS_QUERY from "./graphql/queries/sensorsQuery";

import CURRENT_USER_QUERY from "./graphql/queries/currentUserQuery";
import INVENTORY_ITEMS_LOCATIONS_QUERY from "./graphql/queries/inventoryItemsLocationsQuery";
import INVENTORY_LOCATIONS_QUERY from "./graphql/queries/inventoryLocationsQuery";

import WAITING_SHIPMENT_REQUEST_QUERY from "./graphql/queries/waitingShipmentRequest";
import SUBSCRIBE_TO_WAITING_SHIPMENT_REQUEST from "./graphql/subscriptions/subscribeToWaitingShipmentRequest";
import SUBSCRIBE_TO_AUTOMATION_STATUS from "./graphql/subscriptions/subscribeToAutomationStatus";
import SUBSCRIBE_TO_LOADING_CONCRETE_SHIPMENT from "./graphql/subscriptions/subscribeToLoadingConcreteShipment";

import { CopyButton } from "../../components/CopyButton/CopyButton";
import ShipmentRequestModal from "./ShipmentModal/ShipmentRequestModal.jsx";
import {
  useGetCameraList,
} from "api/controller-configs";

import { CameraWrapper } from "./Camera/CameraWrapper.jsx";
import Notifications from "./Notifications/Notifications.jsx"

import "./InstrumentsPanel.scss";

const PAGE_CLASSES = [".main-panel", ".navbar", ".map-content"];

const CAMERA_HEIGHT = 300;
// const SCREEN_MIN_WIDTH = 0;
const MOBILE_WIDTH = 1000;

const InstrumentsPanel = ({
  sidebarCollapsed,
}) => {
  const [
    shown,
    setShown,
  ] = useState(false);

  const [isSRMOpen, setIsSRMOpen] = useState(false);

  const [expanded, setExpanded] = useState(false);
  const [expansionHeight, setExpansionHeight] = useState(`${CAMERA_HEIGHT}px`);
  const restrictedUsers = new Set(["API_USER", "RESTRICTED_USER"]);
  const isMobile = (window.innerWidth <= MOBILE_WIDTH);
  const { cameraList } = useGetCameraList();

  useEffect(() => {
    document.body.classList.toggle("sidebar-mini");
    document.body.classList.add("instruments-panel");

    PAGE_CLASSES.forEach(el => document.querySelector(el).classList.add("instruments-panel"));

    return () => {
      document.body.classList.toggle("sidebar-mini");
      document.body.classList.add("instruments-panel");

      PAGE_CLASSES.forEach(el => document.querySelector(el).classList.remove("instruments-panel"));
    };
  }, []);

  const [isControlMenuOpen, setIsControlMenuOpen] = useState(false);
  const handleControlMenuOpen = (open) => {
    setIsControlMenuOpen(open);
  };

  const {
    data: loadingConcreteShipmentData,
    refetch,
  } = useQuery(LOADING_CONCRETE_SHIPMENT, {
    // No pollInterval: the loading shipment is now kept live via
    // SUBSCRIBE_TO_LOADING_CONCRETE_SHIPMENT, which refetches this query when a
    // shipment starts, is canceled or finishes. The query still fetches on mount
    // and is resynced on websocket reconnect (see apolloClient.jsx).
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    data: waitingShipmentRequestData,
    refetch: wsrRefetch,
  } = useQuery(WAITING_SHIPMENT_REQUEST_QUERY, {
    // No pollInterval: the waiting shipment request is now kept live via
    // SUBSCRIBE_TO_WAITING_SHIPMENT_REQUEST, which refetches this query when a
    // request is created or cleared. The query still fetches on mount and is
    // resynced on websocket reconnect (see apolloClient.jsx).
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    waitingShipmentRequest,
  } = waitingShipmentRequestData || {};

  const { data: automationStatus } = useQuery(CURRENT_AUTOMATION_STATUS_QUERY, {
    // No pollInterval: the automation status is now kept live via
    // SUBSCRIBE_TO_AUTOMATION_STATUS, which writes each change into this query's
    // cache entry. A single backend poller (AutomationStatusPoller) hits the
    // controller instead of every open panel. The query still fetches on mount
    // and is resynced on websocket reconnect (see apolloClient.jsx).
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const { data: sensorsData } = useQuery(SENSORS_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const { sensors } = sensorsData || [];

  const { data: getCurrentUserData } = useQuery(CURRENT_USER_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const { getCurrentUser } = getCurrentUserData || {};

  const {
    loadingConcreteShipment,
    manualOperations,
  } = loadingConcreteShipmentData || {};

  const {
    data: devicesData,
  } = useQuery(PLANT_LOCATIONS_QUERY, {
    client: httpClient,
    variables: {
      concreteShipmentId: loadingConcreteShipment ? loadingConcreteShipment.id : 0,
    },
    // No pollInterval: actuator state is now kept live via the actuator
    // subscription writing into this query's cache, and resynced on websocket
    // reconnect (see apolloClient.jsx). The query still refetches on mount and
    // whenever the loading shipment changes (variables change).
    fetchPolicy: "network-only",
  });

  const { data: inventoryItemsData } = useQuery(INVENTORY_ITEMS_LOCATIONS_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    inventoryItems,
  } = inventoryItemsData || {};

  const { data: inventoryLocationsData } = useQuery(INVENTORY_LOCATIONS_QUERY, {
    fetchPolicy: "network-only",
    client: httpClient,
  });

  const {
    inventoryLocations,
  } = inventoryLocationsData || {};

  useEffect(() => {
    if (loadingConcreteShipment) {
      if (loadingConcreteShipment.shipmentType === API && !shown) {
        setShown(true);

        AlertModal.fire({
          title: "Carregamento iniciado remotamente",
          type: "info",
          html: (
            <InfoParagraph>
              Não se esqueça de marcar quando o caminhão estiver pronto
            </InfoParagraph>
          ),
          showCancelButton: false,
          confirmButtonColor: "#1D84C6",
          confirmButtonText: "Continuar",
          allowOutsideClick: true,
          allowEscapeKey: true,
          allowEnterKey: true,
        });
      }
    } else {
      setShown(false);
    }
  }, [
    loadingConcreteShipment,
    setShown,
    shown,
  ]);

  useEffect(() => {
    if (waitingShipmentRequest) {
      setIsSRMOpen(true);
    }
  }, [
    waitingShipmentRequest,
  ]);

  const { devices } = devicesData || {};

  const [device] = devices || [];
  const {
    id,
    quantity,
    concreteFormula,
    controllerFinishedAt,
  } = loadingConcreteShipment || {};

  const {
    code,
    fck,
    concreteFamily,
  } = concreteFormula || {};

  const {
    description,
  } = (concreteFamily && concreteFamily.slump) || {};

  const onFinish = () => {
    window.location.reload();
  };

  useEffect(() => {
  }, [loadingConcreteShipment, loadingConcreteShipmentData]);

  return (
    <React.Fragment>
      <ManualOperation
        concreteShipmentId={id}
        controllerHasFinished={!!controllerFinishedAt}
        refetchConcreteShipmentQuery={refetch}
        manualOperations={manualOperations}
        inventoryItemsData={inventoryItemsData}
      />

      <Subscription
        subscription={SUBSCRIBE_TO_WAITING_SHIPMENT_REQUEST}
        onSubscriptionData={() => wsrRefetch()}
        fetchPolicy="no-cache"
      >
        {() => null}
      </Subscription>

      <Subscription
        subscription={SUBSCRIBE_TO_LOADING_CONCRETE_SHIPMENT}
        onSubscriptionData={() => refetch()}
        fetchPolicy="no-cache"
      >
        {() => null}
      </Subscription>

      <Subscription
        subscription={SUBSCRIBE_TO_AUTOMATION_STATUS}
        fetchPolicy="no-cache"
        onSubscriptionData={({ subscriptionData }) => {
          const status = subscriptionData
            && subscriptionData.data
            && subscriptionData.data.subscribeToAutomationStatus;

          if (!status) return;

          httpClient.writeQuery({
            query: CURRENT_AUTOMATION_STATUS_QUERY,
            data: { currentAutomationStatus: status },
          });
        }}
      >
        {() => null}
      </Subscription>

      {!loadingConcreteShipment && waitingShipmentRequest && isSRMOpen && (
        <ShipmentRequestModal
          isOpen={isSRMOpen}
          setIsOpen={setIsSRMOpen}
          inventoryItems={inventoryItems}
          inventoryLocations={inventoryLocations}
          shipmentRequest={waitingShipmentRequest}
        />
      )}

      {
        concreteFormula && (
          <div className="formula-info">
            <div className="code-container">
              <span>{`Código: ${loadingConcreteShipment.code}`}</span>
              <div className="copy-button">
                <CopyButton text={loadingConcreteShipment.code} />
              </div>
            </div>
            <span>{`Traço: ${code}`}</span>
            <span>{`FCK: ${fck} | Slump: ${description}`}</span>
            <span>{`Volume: ${quantity}`}</span>
          </div>
        )
      }

      {
        (getCurrentUser && !restrictedUsers.has(getCurrentUser.role)) && (
          <ControlMenu
            show={!loadingConcreteShipment}
            onRefetch={refetch}
            handleControlMenuOpen={handleControlMenuOpen}
            inventoryItemsData={inventoryItemsData}
            monitoringExpanded={expanded}
            expansionHeight={expansionHeight}
            onClick={() => setExpanded(false)}
          />
        )}

      {(sensors && Boolean(sensors.length)) && (
        <Sensors
          sensorsList={sensors}
          controlMenuOpen={isControlMenuOpen}
          monitoringExpanded={expanded}
          expansionHeight={expansionHeight}
        />
      )}

      <div
        className="map-content"
        style={{
          paddingTop: expanded ? expansionHeight : 0,
        }}
      >
        <InstrumentsPanelWrapper>
          <img
            className="plant-background-image"
            src={device && `${window.location.origin}${device.plantImagePath}`}
            alt="Painel de instrumentos"
            style={{
              position: "relative",
              top: 0,
              left: 0,
              width: "100%",
              height: "auto",
            }}
          />
          <InstrumentsPanelContent
            refetchConcreteShipmentQuery={refetch}
            onFinish={onFinish}
            loadingShipment={loadingConcreteShipment}
            plantLocations={device ? device.plantLocations : []}
            automationStatus={automationStatus}
          />
        </InstrumentsPanelWrapper>
      </div>
      {(cameraList.length > 0 && sidebarCollapsed) && (
        <CameraWrapper
          expanded={expanded}
          setExpanded={setExpanded}
          setExpansionHeight={setExpansionHeight}
          expansionHeight={expansionHeight}
          height={CAMERA_HEIGHT}
          cams={cameraList}
          isMobile={isMobile}
        />
      )}
      <Notifications />
    </React.Fragment>
  );
};

export default InstrumentsPanel;
